import { isPlainObject, mapValues } from 'lodash-es';

/**
 * A presigned link to a report payload (reporting-api `GET /v1/reports/{id}/data`, then S3 or its CDN):
 * `reports/{id}/data.json`, or a quote-free copy under `reports/{id}/redacted/`. It reads the payload
 * for as long as it is valid, quotes included, so no telemetry SDK (Datadog RUM, Datadog Logs, Sentry)
 * may ship it with its signature (CORE-569).
 */
export const isReportDataSignedUrl = (url: string | undefined): boolean => {
  if (!url) return false;

  try {
    const { pathname, searchParams } = new URL(url);

    return (
      // Unanchored, so a path-style URL (`/{bucket}/reports/…`) matches as well as the CDN's.
      searchParams.has('X-Amz-Signature') && /\/reports\/[^/]+\/(data\.json$|redacted\/)/.test(pathname)
    );
  } catch {
    return false;
  }
};

const URL_IN_TEXT = /https?:\/\/[^\s"'<>]+/g;

// A signed payload link has a query, and URL parsing keeps a path's percent-encoding, so its text always holds
// a `?` and a literal `reports`. Text missing either is skipped without parsing every URL in it, which matters
// because the telemetry callbacks run this on every event.
const mayQuoteReportDataSignedUrl = (text: string): boolean => text.includes('?') && text.includes('reports');

/** Whether free text (a log message, a span name) quotes a signed report payload link. */
export const containsReportDataSignedUrl = (text: string | undefined): boolean =>
  !!text && mayQuoteReportDataSignedUrl(text) && (text.match(URL_IN_TEXT) ?? []).some(isReportDataSignedUrl);

/** The link without its query (and so without the signature), or the input unchanged when it is not one. */
export const stripReportDataSignature = (url: string): string => {
  if (!isReportDataSignedUrl(url)) return url;

  const parsed = new URL(url);

  return `${parsed.origin}${parsed.pathname}`;
};

/** Free text with every signed report payload link it quotes cut down to its origin and path. */
export const stripReportDataSignaturesInText = (text: string): string =>
  mayQuoteReportDataSignedUrl(text) ? text.replace(URL_IN_TEXT, (url) => stripReportDataSignature(url)) : text;

/**
 * An event or context with signed report payload links stripped from every string in it. Never changes its
 * input: a part that held a link is copied, and a part that held none is returned as it was.
 */
export const stripReportDataSignaturesDeep = <T>(value: T): T => {
  if (typeof value === 'string') return stripReportDataSignaturesInText(value) as T;
  if (Array.isArray(value)) {
    const items: unknown[] = value;
    const stripped = items.map((item) => stripReportDataSignaturesDeep(item));

    return (stripped.some((item, index) => item !== items[index]) ? stripped : value) as T;
  }
  if (isPlainObject(value)) {
    const fields = value as Record<string, unknown>;
    const stripped = mapValues(fields, (item) => stripReportDataSignaturesDeep(item));

    return (Object.keys(fields).some((key) => stripped[key] !== fields[key]) ? stripped : value) as T;
  }

  return value;
};

// A run of address characters, with the `@domain` that makes it an email address when one follows. The
// domain needs a dot before its end, which leaves Firefox and Safari stack frames
// (`fn@https://host/file.js:1:2`) alone. Each run is read once, so a long one without an `@` (a base64
// blob in a console error) costs linear time; an unanchored `local@domain` pattern costs quadratic.
const EMAIL_IN_TEXT = /[\w.%+-]+(@[a-z\d-]+(?:\.[a-z\d-]+)*\.[a-z]{2,})?/gi;

/**
 * RUM error text (a message, a stack) without signed report payload links or email addresses. Error
 * Tracking alerts quote the message, and server errors such as "User … has not been invited to
 * organization" carry the signed-in user's email.
 */
export const sanitizeRumErrorText = (text: string): string =>
  stripReportDataSignaturesInText(text).replace(EMAIL_IN_TEXT, (run, domain?: string) => (domain ? '[email]' : run));

/**
 * The error as RUM may receive it: the same instance when its message, stack and cause chain quote no
 * signed report payload link or email address, otherwise a copy without them that keeps its `name`.
 * The original is never changed, as the caller may still be using it.
 */
export const sanitizeErrorForRum = (error: Error): Error => {
  const message = sanitizeRumErrorText(error.message);
  const stack = error.stack === undefined ? undefined : sanitizeRumErrorText(error.stack);
  const cause = error.cause instanceof Error ? sanitizeErrorForRum(error.cause) : error.cause;

  if (message === error.message && stack === error.stack && cause === error.cause) return error;

  const copy = new Error(message, { cause });
  copy.name = error.name;
  copy.stack = stack;

  return copy;
};
